/**
 * Grid System Configuration
 *
 * This configuration serves as the single source of truth for the Map3Dependencies and Map3Relatives grid system.
 * It defines all measurements and styling used to create a consistent grid across both static
 * and interactive elements.
 *
 * Design Decisions:
 * 1. Single Grid System:
 *    - Uses ReactFlow's Background component as the sole grid visualization
 *    - Eliminates duplicate grid implementations to prevent visual misalignment
 *    - All static elements (headers, labels) align with this grid
 *
 * 2. Cell Dimensions:
 *    - 40x40px cells provide enough space for status indicators while maintaining density
 *    - Matches Map3's existing table cell height for visual consistency
 *    - Width allows ~3-4 characters of text in a result node
 *
 * 3. Layout Structure:
 *    - 200px objective column width accommodates typical objective text length
 *    - 80px header height (2 cells) provides space for date display
 *    - Coordinate system starts at (0,0) in top-left corner
 *
 * 4. Z-Index Layering:
 *    - Static elements (grid): 10 - Forms the base layer
 *    - Interactive nodes: 20 - Allows interaction above grid
 *    - Edges/controls: 30 - Ensures visibility above nodes
 */

import type { MilestoneMapInterval } from '../types';

export const GRID_CONFIG = {
  // Core dimensions
  cellWidth: 40,
  cellHeight: 40,
  headerHeight: 80, // 2 * cellHeight for date display
  objectiveWidth: 200,

  // Visual styling
  colors: {
    border: 'rgb(229, 231, 235)', // Tailwind gray-200
    background: 'white',
  },

  // Layer management
  zIndex: {
    grid: 10, // Background grid and static elements
    maps: 15, // Map nodes (entire milestone map container and status cells)
    collapsedNodes: 12, // Result nodes behind corresponding map node's title bars (not interactive)
    nodes: 20, // Interactive result nodes
    edges: 30, // Dependency connections, React Flow calls these edges
    collapsedMaps: 35, // Map nodes when collapsed (above edges)
    toolbar: 40, // Toolbar buttons and controls for ReactFlow NodeToolbar, etc.
    tooltips: 50000, // Tooltips - must be above all React Flow internal z-index values
  },
} as const; // Make this configuration immutable

// Export type for use in components
export type GridConfig = typeof GRID_CONFIG;

/**
 * Generates an array of intervals (columns) for the given interval type.
 * All intervals are uniform and use the fixed cell width from GRID_CONFIG.
 */
export function generateIntervals(
  start: Date,
  end: Date,
  intervalType: MilestoneMapInterval,
): Array<{ start: Date; end: Date }> {
  const intervals: { start: Date; end: Date }[] = [];
  let current = new Date(start);
  let idx = 0;
  function addInterval(date: Date, type: MilestoneMapInterval): Date {
    const d = new Date(date);
    switch (type) {
      case 'days':
        // Returns the next day (interval: [current day, next day))
        d.setDate(d.getDate() + 1);
        break;
      case 'weeks':
        // Returns the same day of the week, one week later (interval: [current week start, next week start))
        d.setDate(d.getDate() + 7);
        break;
      case 'months':
        // Returns the same day of the next month (interval: [current month start, next month start))
        d.setMonth(d.getMonth() + 1);
        break;
      case 'quarters':
        // Returns the same day, three months later (interval: [current quarter start, next quarter start))
        d.setMonth(d.getMonth() + 3);
        break;
      case 'years':
        // Returns the same day and month, one year later (interval: [current year start, next year start))
        d.setFullYear(d.getFullYear() + 1);
        break;
      default:
        throw new Error('Unsupported interval type: ' + type);
    }
    return d;
  }
  while (current < end) {
    const next = addInterval(current, intervalType);
    intervals.push({ start: new Date(current), end: new Date(next) });
    current = next;
    idx++;
    // Safety: break if too many intervals
    if (idx > 1000) throw new Error('Too many intervals generated in generateIntervals');
  }
  return intervals;
}
